import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import type { MenuItem } from '@/api/types';
import { useAuth } from '@/auth/useAuth';

/**
 * The sidebar tree from `GET /api/navigation`.
 *
 * Permission trimming stays on the server — `ILocalizedMenuBuilder.BuildAsync`
 * already drops items the user may not see, and titles arrive translated for
 * the request culture. The client must not re-derive either, or the menu could
 * disagree with what the endpoints actually allow.
 */

export const NAVIGATION_QUERY_KEY = ['navigation'] as const;

export function useNavigation() {
  const { isAuthenticated, user } = useAuth();
  const { i18n } = useTranslation();

  return useQuery<MenuItem[]>({
    // Keyed by user and language: two accounts on the same browser must not
    // share a permission-trimmed menu, and the titles are translated server
    // side, so switching language has to refetch rather than reuse the cache.
    queryKey: [...NAVIGATION_QUERY_KEY, user?.id ?? null, i18n.language],
    queryFn: () => api.get<MenuItem[]>('/api/navigation'),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });
}
